import { z } from 'zod';
import { LineItemType } from 'shared/src/domain/line-item/line-item-types';
import { refineBasePacing, refinePacingTotal } from 'shared/src/api/line-item-types-utils';
import { ISODateRange, ISODateRangeFromDB } from 'shared/src/date-string-utils/types';
import { UnitPriceTypeNames } from 'shared/src/domain/line-item/line-item-channels';

export const PacingBlockV2 = z.object({
  dates: ISODateRange,
  budget: z.number('Price is required')
});
export type PacingBlockV2 = z.infer<typeof PacingBlockV2>;

export const PacingBlockV2FromDB = z.object({
  dates: ISODateRangeFromDB,
  budget: z.number('Price is required')
});
export type PacingBlockV2FromDB = z.infer<typeof PacingBlockV2FromDB>;

export const BaseLineItem = z.object({
  name: z.string('Name is required').min(1, 'Name is required'),
  price: z.number('Price is required'),
  channel_id: z.number('Channel must be selected'),
  tactic_id: z.number('Tactic must be selected'),
  unit_price_type_id: z.number('Unit price type must be selected'),
  dates: ISODateRange,
  pacing_type: z.union([z.literal('lifetime'), z.literal('custom')]),
  pacing_details: z.array(PacingBlockV2),
  monthly_rollover: z.boolean(),
  geo: z.string('Geo must be set').min(1, 'Geo must be set'),
  targeting: z.string().optional(),
  audience: z.string().optional(),
  ad_formats: z.string().optional(),
  notes: z.string().max(1000, 'Team notes must be 1000 characters or less').nullish(),
  media_platform_ids: z.array(z.number()).optional(),
  planned_unit_cost: z
    .number('Planned unit cost must be defined')
    .nonnegative('Planned unit cost must be zero or greater'), // CPM values are stored per unit so / 1000
  purchase_order_number: z
    .string()
    .regex(/^\d+$/, 'Purchase order number must be numeric')
    .nullable()
    .optional()
});

export type BaseLineItem = z.infer<typeof BaseLineItem>;

// safeExtend ensures that all refinements on the base schema are still applied
export const StandardLineItemInput = BaseLineItem.safeExtend({
  line_item_type: z.literal('standard'),
  unit_price: z.number('Unit price is required').nonnegative('Unit price must be zero or greater')
}).superRefine((data, ctx) => {
  // Order is important here
  refinePacingTotal(data, data.price, ctx);
  refineBasePacing(data, ctx);
});
export type StandardLineItemInput = z.infer<typeof StandardLineItemInput>;

export const MgmtFeeLineItemInput = BaseLineItem.safeExtend({
  line_item_type: z.literal('management_fee'),
  media_budget: z.number()
}).superRefine((data, ctx) => {
  // Order is important here
  refinePacingTotal(data, data.media_budget, ctx);
  refineBasePacing(data, ctx);
});

export type MgmtFeeLineItemInput = z.infer<typeof MgmtFeeLineItemInput>;

export const ZeroDollarLineItemInput = BaseLineItem.safeExtend({
  line_item_type: z.literal('zero_dollar'),
  estimated_units: z.number('Estimated units is required')
}).superRefine((data, ctx) => {
  // API/DB wire format: planned_unit_cost is per-unit (e.g., 0.001495 for $1.495 CPM).
  // Form-side CPM-display conversion lives in ZeroDollarLineItemEditingSchema.
  const pacingAmount =
    data.estimated_units !== undefined && data.planned_unit_cost !== undefined
      ? data.estimated_units * data.planned_unit_cost
      : undefined;
  // Order is important here
  refinePacingTotal(data, pacingAmount, ctx);
  refineBasePacing(data, ctx);
});
export type ZeroDollarLineItemInput = z.infer<typeof ZeroDollarLineItemInput>;

export const LineItemInput = z.discriminatedUnion('line_item_type', [
  StandardLineItemInput,
  MgmtFeeLineItemInput,
  ZeroDollarLineItemInput
]);
export type LineItemInput = z.infer<typeof LineItemInput>;

// Read-only version of a line item that includes fields generated by the DB
export const LineItem = BaseLineItem.safeExtend({
  id: z.string(),
  strategy_id: z.string(),
  line_item_type: LineItemType,
  unit_price: z.coerce.number().nullable(),
  target_margin: z.coerce.number(),
  target_unit_price: z.coerce.number(),
  target_unit_cost: z.coerce.number(),
  planned_margin: z.coerce.number(),
  media_budget: z.coerce.number(),
  estimated_units: z.coerce.number(),
  effective_price: z.coerce.number(),
  gross_price: z.coerce.number(),
  gross_unit_price: z.coerce.number().nullable(),
  effective_unit_price: z.coerce.number().nullable(),
  agency_markup_rate: z.coerce.number(),
  referral_commission_rate: z.coerce.number(),
  referral_commission_amount: z.coerce.number(),
  channel_name: z.string(),
  tactic_name: z.string(),
  unit_price_type_name: UnitPriceTypeNames
});

export type LineItem = z.infer<typeof LineItem>;

// Media Trader Line Item Update schema - allows only updating
// media_platform_ids or planned_unit_cost
// NOTE: monthly_rollover is excluded - only account managers/admins can modify pacing settings
export const MediaTraderLineItemUpdate = BaseLineItem.omit({
  name: true,
  price: true,
  channel_id: true,
  tactic_id: true,
  unit_price_type_id: true,
  dates: true,
  pacing_type: true,
  pacing_details: true,
  monthly_rollover: true,
  geo: true,
  targeting: true,
  audience: true,
  ad_formats: true,
  purchase_order_number: true,
  notes: true
});
export type MediaTraderLineItemUpdate = z.infer<typeof MediaTraderLineItemUpdate>;
